/**
 * i18next 초기화 (M14 Phase 0 — bilingual JP/KO).
 *
 * 정책:
 *   - **default = 일본어** (일본 거래처용 인보이스 시스템).
 *     ``fallbackLng: "ja"`` — 키 미정의 시에도 일본어 노출 (한국어 누락
 *     이 사용자에게 보이지 않도록).
 *   - **언어 감지 우선순위**: localStorage(``"ttdc.lang"``) → ``navigator.
 *     language`` → fallback "ja".
 *     사용자가 ``LanguageToggle`` 로 명시 선택하면 localStorage 에 저장
 *     되어 다음 세션부터 즉시 적용.
 *   - **namespace 단일** (``"common"``) — Phase 1/2/3 에서 키가 폭증하면
 *     namespace 를 분리 (jobs, auth, admin 등) 한다.
 *   - **Suspense 비활성** (``react: { useSuspense: false }``) — 번역 리소스
 *     를 정적 import 하므로 비동기 로딩 단계 자체가 없음. Suspense 를
 *     키면 SSR/CSR 기본 boundary 가 필요해 불필요한 복잡도 추가.
 *   - **escapeValue: false** — React 가 이미 XSS 이스케이프 처리. i18next
 *     공식 권장.
 *
 * Phase 0 에서는 토글 인프라와 smoke 키 1-2개만 등록.
 * 본격적인 번역 키는 Phase 1 (핵심 화면) 부터 추가한다.
 */

import i18n from "i18next";
import LanguageDetector from "i18next-browser-languagedetector";
import { initReactI18next } from "react-i18next";

import ja from "./locales/ja.json";
import ko from "./locales/ko.json";

/** localStorage 키 — detector + LanguageToggle 양쪽이 공유. */
export const LANG_STORAGE_KEY = "ttdc.lang";

/** 지원 언어 목록 — 타입 안전한 토글에 사용. */
export const SUPPORTED_LANGS = ["ja", "ko"] as const;
export type SupportedLang = (typeof SUPPORTED_LANGS)[number];

void i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources: {
      ja: { common: ja },
      ko: { common: ko },
    },
    fallbackLng: "ja",
    defaultNS: "common",
    supportedLngs: SUPPORTED_LANGS,
    nonExplicitSupportedLngs: true, // ja-JP, ko-KR 도 ja, ko 로 흡수
    interpolation: {
      escapeValue: false,
    },
    detection: {
      order: ["localStorage", "navigator"],
      caches: ["localStorage"],
      lookupLocalStorage: LANG_STORAGE_KEY,
    },
    react: {
      useSuspense: false,
    },
  });

export default i18n;
